import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { DayPicker } from "react-day-picker";
import { es, enGB, de, it } from "date-fns/locale";
import { Link } from "react-router-dom";
import { useLang } from "../i18n";
import { extras as extraCopy } from "../i18n";
import { API, DEPOSIT_EUR, EXTRAS_META, extraPrice, quote, computeBilling, fastCheckLicense, LICENSE_TYPES, bikeIsRideable, bikeMinAge, WHATSAPP_NUMBER } from "../config";
import { X, ChevronLeft, CheckCircle2, Loader2, TicketPercent, HeartHandshake, IdCard, ShieldCheck, ShieldAlert, Hand, Package, Truck, Store, Shirt, Smartphone, MessageCircle, Flame } from "lucide-react";
import { HelmetIcon } from "./Header";

const ALL_HOURS = ["09:00", "09:30", "10:00", "10:30", "11:00", "11:30", "12:00", "12:30", "13:00", "13:30", "14:00", "15:00", "16:00", "16:30", "17:00", "17:30"];
// horario de tienda: L-V hasta 17:30 · sábados hasta 13:30 · domingos y festivos (25 dic, 1 y 6 ene) cerrado
const SAT_LAST = "13:30";
const WEEK_LAST = "17:30";
const CLOSED_MD = ["12-25", "01-01", "01-06"];
const isClosedDate = (d) => {
  if (!d) return false;
  if (d.getDay() === 0) return true;
  return CLOSED_MD.includes(`${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
};
const hoursFor = (d) => (d && d.getDay() === 6 ? ALL_HOURS.filter((h) => h <= SAT_LAST) : ALL_HOURS.filter((h) => h <= WEEK_LAST));

const EXTRA_ICONS = {
  helmet2: { Icon: HelmetIcon, svg: true },
  gloves: { Icon: Hand },
  jacket1: { Icon: Shirt },
  jacket2: { Icon: Shirt },
  phone_holder: { Icon: Smartphone },
  topcase: { Icon: Package },
  full_insurance: { Icon: ShieldCheck },
  delivery: { Icon: Truck },
};
const bikeImg = (b) => (b.image_path ? `${API}/bikes/image/${b.slug}` : b.image);

const EXTRA_KEYS = Object.keys(EXTRAS_META);
const LOCALES = { es, en: enGB, de, it };
const fmt = (n) => `${Math.round(n * 100) / 100}€`;
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const nfDays = (n) => (Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100).replace(".", ","));
const parseISO = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };

export default function BookingModal({ bike, bikes, open, onClose }) {
  const { t, lang } = useLang();
  const x = extraCopy[lang] || extraCopy.es;
  const [step, setStep] = useState(0);
  const [slug, setSlug] = useState(null);
  const [range, setRange] = useState();
  const [booked, setBooked] = useState([]);
  const [avail, setAvail] = useState({ stock: 1, counts: {} });
  const [pickup, setPickup] = useState("office");
  const [pickupTime, setPickupTime] = useState("09:00");
  const [returnTime, setReturnTime] = useState("17:30");
  const [hotelName, setHotelName] = useState("");
  const [hotelOpen, setHotelOpen] = useState(false);
  const [extras, setExtras] = useState([]);
  const [deliveryEnabled, setDeliveryEnabled] = useState(false);
  const [closedDates, setClosedDates] = useState(() => new Set());
  const [customHours, setCustomHours] = useState({});
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [check, setCheck] = useState({ birthdate: "", licenseType: "", licenseYear: "" });
  const [loyalty, setLoyalty] = useState(null);
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [terms, setTerms] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [doneRef, setDoneRef] = useState(null);
  const [doneBooking, setDoneBooking] = useState(null);
  const [docsBooking, setDocsBooking] = useState(null);
  const [docFiles, setDocFiles] = useState({});
  const [docsConsent, setDocsConsent] = useState(false);
  const pDocs = (extraCopy[lang] || extraCopy.es).precheckin;

  const bookable = useMemo(() => bikes.filter((b) => bikeIsRideable(b) && !b.coming_soon), [bikes]);
  // sin moto preseleccionada (botón BOOK NOW genérico): usa la primera del desplegable
  useEffect(() => {
    if (open && !slug && !bike && bookable.length) setSlug(bookable[0].slug);
  }, [open, slug, bike, bookable]);
  // al abrir de nuevo, limpia el paso de documentos
  useEffect(() => {
    if (open) { setDocsBooking(null); setDocFiles({}); setDocsConsent(false); }
  }, [open]);
  const selected = useMemo(
    () => bookable.find((b) => b.slug === (slug || bike?.slug)) || bike,
    [bookable, slug, bike]
  );
  const checkResult = useMemo(
    () => (selected?.license ? fastCheckLicense(check, selected.license, bikeMinAge(selected)) : null),
    [check, selected]
  );

  const start = range?.from ? iso(range.from) : "";
  const end = range?.to ? iso(range.to) : "";
  // horas disponibles un día concreto, aplicando cierres especiales (ej. 12-oct hasta 14:00)
  const hoursForDate = (d) => {
    const base = hoursFor(d);
    if (!d) return base;
    const close = customHours[iso(d)];
    return close ? base.filter((h) => h <= close) : base;
  };
  // si la fecha elegida recorta el horario (sábado o cierre especial), baja la hora a la última válida
  useEffect(() => {
    const opts = hoursForDate(range?.from);
    if (opts.length && !opts.includes(pickupTime)) setPickupTime(opts[opts.length - 1]);
  }, [range?.from, customHours]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const opts = hoursForDate(range?.to);
    if (opts.length && !opts.includes(returnTime)) setReturnTime(opts[opts.length - 1]);
  }, [range?.to, customHours]); // eslint-disable-line react-hooks/exhaustive-deps
  const billing = useMemo(
    () => computeBilling(start, end, pickupTime, returnTime),
    [start, end, pickupTime, returnTime]
  );
  const days = billing.billable;
  const unitsLeft = useMemo(() => {
    if (!range?.from || !range?.to || !selected) return null;
    let maxC = 0, d = new Date(range.from);
    while (d < range.to) {
      maxC = Math.max(maxC, avail.counts[iso(d)] || 0);
      const n = new Date(d); n.setDate(n.getDate() + 1); d = n;
    }
    return Math.max((avail.stock || 1) - maxC, 0);
  }, [range, avail, selected]);
  const loyaltyPct = loyalty?.returning && !coupon ? loyalty.percent : null;
  // la entrega en hotel (+25€) va unida al punto de recogida, no es un extra suelto
  const effectiveExtras = useMemo(
    () => {
      let list = pickup === "hotel" && !extras.includes("delivery") ? [...extras, "delivery"] : extras;
      // norma de la casa: todo riesgo solo con 25 años y 3 de carnet
      if (checkResult?.noFullInsurance) list = list.filter((e) => e !== "full_insurance");
      return list;
    },
    [pickup, extras, checkResult]
  );
  const price = useMemo(
    () => (selected && days > 0 ? quote(selected, days, effectiveExtras, coupon, loyaltyPct, billing.tierDays) : null),
    [selected, days, effectiveExtras, coupon, loyaltyPct, billing.tierDays]
  );

  useEffect(() => {
    setRange(undefined);
    setBooked([]);
    if (selected?.slug) {
      axios.get(`${API}/bikes/${selected.slug}/availability`)
        .then(({ data }) => {
          // el backend ya calcula los días sin stock (ocupación >= unidades del modelo)
          const full = (data.full_dates || []).map(parseISO);
          setBooked(full);
          setAvail({ stock: data.stock || 1, counts: data.day_counts || {} });
        })
        .catch(() => {});
    }
  }, [selected?.slug]);

  useEffect(() => {
    axios.get(`${API}/settings/public`)
      .then(({ data }) => {
        const enabled = !!data.delivery_pickup_enabled;
        setDeliveryEnabled(enabled);
        setClosedDates(new Set(data.closed_dates || []));
        setCustomHours(data.custom_hours || {});
        if (!enabled) {
          setPickup("office");
          setHotelName("");
          setHotelOpen(false);
        }
      })
      .catch(() => setDeliveryEnabled(false));
  }, []);


  if (!open) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const currentYear = new Date().getFullYear();

  const checkLoyalty = async (email) => {
    if (!email || !email.includes("@")) return;
    try {
      const { data } = await axios.post(`${API}/loyalty/check`, { email });
      setLoyalty(data.returning ? data : null);
      if (data.returning) toast.success(x.loyalty);
    } catch {}
  };

  const rangeHasBookedDay = () => {
    if (!range?.from || !range?.to) return false;
    const set = new Set(booked.map(iso));
    let d = new Date(range.from);
    while (d < range.to) {
      if (set.has(iso(d))) return true;
      d.setDate(d.getDate() + 1);
    }
    return false;
  };

  const toggleExtra = (k) =>
    setExtras((prev) => (prev.includes(k) ? prev.filter((e) => e !== k) : [...prev, k]));

  const applyCoupon = async () => {
    if (!couponInput.trim()) return;
    try {
      const { data } = await axios.post(`${API}/coupons/validate`, { code: couponInput.trim() });
      setCoupon(data);
      toast.success(`${t.booking.couponApplied}: ${data.code}`);
    } catch {
      setCoupon(null);
      toast.error(t.booking.couponInvalid);
    }
  };

  const createBooking = async () => {
    const { data } = await axios.post(`${API}/bookings`, {
      bike_slug: selected.slug, start_date: start, end_date: end,
      pickup, extras: effectiveExtras,
      hotel_name: pickup === "hotel" ? hotelName.trim() : null,
      ...form, lang,
      coupon_code: coupon?.code || null, accepted_terms: terms, accepted_privacy: privacy,
      license_type: check.licenseType || null,
      license_year: Number(check.licenseYear) || null,
      birthdate: check.birthdate || null,
      pickup_time: pickupTime,
      return_time: returnTime,
    });
    return data.booking;
  };

  const submit = async (withDeposit) => {
    if (!terms) {
      toast.error(t.booking.termsRequired);
      return;
    }
    if (!privacy) {
      toast.error(x.privacyRequired);
      return;
    }
    setBusy(true);
    try {
      const booking = await createBooking();
      if (withDeposit) {
        // norma del cliente: documentación escaneada obligatoria ANTES de pagar
        if (booking.precheckin_status === "pending" && booking.precheckin_token) {
          setDocsBooking(booking);
          return;
        }
        const { data } = await axios.post(`${API}/payments/checkout`, {
          booking_ref: booking.ref, origin_url: window.location.origin,
        });
        window.location.href = data.checkout_url;
        return;
      }
      setDoneRef(booking.ref);
      setDoneBooking(booking);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail === "dates_unavailable") toast.error(t.booking.unavailable);
      else if (detail === "terms_required") toast.error(t.booking.termsRequired);
      else if (detail === "privacy_required") toast.error(x.privacyRequired);
      else if (detail === "bike_unavailable") toast.error(x.notAvailable);
      else if (detail === "hotel_required") toast.error(t.booking.hotelRequired);
      else if (detail === "docs_required") toast.error(x.docsRequiredTitle);
      else if (detail === "fast_check_min_age") toast.error(x.fastCheck.failMinAge);
      else if (detail === "fast_check_insurance_age") toast.error(x.under25NoInsurance);
      else if (detail === "payment_unavailable" || detail === "payment_error") toast.error(t.booking.paymentUnavailable);
      else toast.error(t.booking.error);
    } finally {
      setBusy(false);
    }
  };

  const uploadDocsAndPay = async () => {
    const missing = ["dni_front", "dni_back", "carnet_front", "carnet_back"].filter((k) => !docFiles[k]);
    if (missing.length) {
      toast.error(pDocs.required);
      return;
    }
    if (!docsConsent) {
      toast.error(pDocs.consentRequired);
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("falsity_consent", "true");
      ["dni_front", "dni_back", "carnet_front", "carnet_back"].forEach((k) => fd.append(k, docFiles[k]));
      await axios.post(`${API}/precheckin/${docsBooking.precheckin_token}`, fd);
      const { data } = await axios.post(`${API}/payments/checkout`, {
        booking_ref: docsBooking.ref, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      const code = e.response?.data?.detail;
      if (code === "too_big") toast.error("Archivo demasiado grande (máx. 10 MB)");
      else if (code === "bad_type") toast.error("Solo imágenes o PDF");
      else if (code === "upload_failed") toast.error(t.booking.uploadFailed);
      else if (code === "payment_unavailable" || code === "payment_error") toast.error(t.booking.paymentUnavailable);
      else toast.error(t.booking.error);
      setBusy(false);
    }
  };

  const next = () => {
    if (step === 0) {
      if (!start || !end) {
        toast.error(t.booking.invalidDates);
        return;
      }
      if (rangeHasBookedDay()) {
        toast.error(t.booking.unavailable);
        return;
      }
    }
    if (step === 1) {
      if (!form.name.trim() || !form.email.includes("@") || !form.phone.trim()) {
        toast.error(x.fastCheck.fillData);
        return;
      }
      if (!/^\+\d[\d\s().-]{5,}$/.test(form.phone.trim())) {
        toast.error(x.fastCheck.phonePrefix);
        return;
      }
      if (!checkResult?.ok) {
        toast.error(checkResult?.code === "min_age" ? x.fastCheck.failMinAge : x.fastCheck.blocked);
        return;
      }
    }
    setStep(step + 1);
  };

  const checkMsg = checkResult == null
    ? null
    : checkResult.ok
      ? x.fastCheck.ok
      : checkResult.code === "min_age"
        ? x.fastCheck.failMinAge
        : checkResult.code === "b3"
          ? x.fastCheck.failB3
          : checkResult.code === "age"
            ? x.fastCheck.failAge
            : x.fastCheck.failType.replace("{req}", selected?.license || "");

  const labelCls = "block text-xs uppercase tracking-wider text-topacio-faded font-extrabold mb-2";

  return (
    <div className="fixed inset-0 z-[90] flex items-stretch sm:items-center justify-center p-0 sm:p-4" data-testid="booking-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative card-brutal shadow-[8px_8px_0_0_#111] !rounded-none sm:!rounded-xl w-full sm:max-w-xl h-full sm:h-auto sm:max-h-[90vh] overflow-y-auto" data-lenis-prevent>
        <div className="flex items-center justify-between px-6 py-4 border-b-2 border-topacio-ink sticky top-0 bg-white z-10 sm:rounded-t-xl">
          <h3 className="font-display font-bold text-topacio-ink">{t.booking.title}</h3>
          <button data-testid="booking-close-btn" onClick={onClose} className="text-topacio-faded hover:text-topacio-ink transition-colors p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        {docsBooking ? (
          <div className="p-5 sm:p-6" data-testid="docs-step">
            <div className="flex items-start gap-3 mb-5">
              <span className="w-10 h-10 bg-brand border-2 border-topacio-ink rounded-lg flex items-center justify-center shrink-0"><IdCard className="w-5 h-5" /></span>
              <div>
                <h4 className="font-display font-bold text-topacio-ink text-lg">{x.docsRequiredTitle}</h4>
                <p className="text-xs text-topacio-faded font-semibold mt-1">{x.docsRequiredSub}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[["dni_front", `${pDocs.identity} · ${pDocs.front}`], ["dni_back", `${pDocs.identity} · ${pDocs.back}`],
                ["carnet_front", `${pDocs.license} · ${pDocs.front}`], ["carnet_back", `${pDocs.license} · ${pDocs.back}`]].map(([id, label]) => (
                <label key={id} data-testid={`docs-upload-${id}`}
                  className={`cursor-pointer rounded-lg border-2 border-dashed p-3 text-center transition-colors ${docFiles[id] ? "border-topacio-green bg-topacio-green/5" : "border-zinc-300 hover:border-topacio-ink"}`}>
                  <input type="file" accept="image/*,application/pdf" capture="environment" className="hidden"
                    onChange={(e) => setDocFiles({ ...docFiles, [id]: e.target.files[0] || null })} />
                  <p className="text-[10px] font-extrabold uppercase tracking-wider text-topacio-faded leading-snug">{label}</p>
                  <p className={`text-[11px] font-extrabold mt-1.5 ${docFiles[id] ? "text-topacio-green" : "text-topacio-red"}`}>
                    {docFiles[id] ? `✓ ${docFiles[id].name.slice(0, 18)}` : pDocs.upload}
                  </p>
                </label>
              ))}
            </div>
            <label data-testid="docs-consent" className="mt-4 flex items-start gap-3 rounded-lg border-2 border-zinc-200 p-3.5 cursor-pointer">
              <input type="checkbox" checked={docsConsent} onChange={(e) => setDocsConsent(e.target.checked)} className="w-5 h-5 mt-0.5 accent-[#FFCC00] shrink-0" />
              <span className="text-[11px] font-semibold text-topacio-ink leading-relaxed">{pDocs.consent}</span>
            </label>
            <p className="mt-3 text-[10px] text-topacio-faded font-semibold">{x.docsFallbackNote}</p>
            <button data-testid="docs-pay-btn" onClick={uploadDocsAndPay} disabled={busy}
              className="mt-5 btn-primary w-full !py-4 !text-sm disabled:opacity-50">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShieldCheck className="w-5 h-5" />}
              {busy ? pDocs.sending : x.docsPay}
            </button>
          </div>
        ) : doneRef ? (
          <div className="p-8 text-center">
            <CheckCircle2 className="w-14 h-14 text-topacio-green mx-auto" strokeWidth={2.2} />
            <h4 className="font-display font-bold text-topacio-ink text-xl mt-4">{t.booking.success}</h4>
            <p className="text-topacio-faded text-sm mt-2">{t.booking.successSub}</p>
            <p data-testid="booking-ref" className="font-display font-black text-topacio-red text-2xl mt-4">{doneRef}</p>
            <p data-testid="precheckin-email-note" className="text-topacio-faded text-xs mt-3">
              {doneBooking?.precheckin_status === "pending" ? x.precheckinEmailPending
                : doneBooking?.precheckin_status === "completed" ? x.precheckinEmailDone
                : t.booking.emailNote}
            </p>
            <button data-testid="booking-done-btn" onClick={onClose} className="mt-8 btn-primary !px-8">OK</button>
          </div>
        ) : (
          <div className="p-5 sm:p-6">
            <div className="flex gap-2 mb-8">
              {[t.booking.step1, t.booking.step2, t.booking.step3].map((s, i) => (
                <div key={i} className={`flex-1 text-center text-[10px] uppercase tracking-wider font-extrabold pb-2 border-b-[3px] transition-colors ${step >= i ? "border-brand text-topacio-ink" : "border-zinc-200 text-zinc-400"}`}>
                  {s}
                </div>
              ))}
            </div>

            {step === 0 && (
              <div className="space-y-6">
                <div>
                  <label className={labelCls}>{t.fleet.eyebrow}</label>
                  <select data-testid="booking-bike-select" value={selected?.slug || ""} onChange={(e) => setSlug(e.target.value)} className="input-brutal">
                    {bookable.map((b) => (
                      <option key={b.slug} value={b.slug}>{`${b.name} — ${b.price_1d}€${t.fleet.day}`}</option>
                    ))}
                  </select>
                </div>
                {selected && (
                  <div data-testid="booking-bike-preview" className="flex gap-4 items-center border-2 border-topacio-ink rounded-lg p-3 bg-topacio-sand shadow-[3px_3px_0_0_#111]">
                    <img src={bikeImg(selected)} alt={selected.name}
                      className="w-28 h-20 object-cover rounded-md border-2 border-topacio-ink bg-white shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="font-display font-bold text-topacio-ink text-sm leading-tight">{selected.name}</div>
                      <div className="text-[11px] text-topacio-faded font-semibold mt-1">{selected.cc}cc · {t.fleet.license} {selected.license}</div>
                      <div className="font-display font-black text-topacio-red text-base mt-1">{selected.price_1d}€<span className="text-[10px] text-topacio-faded font-body font-semibold">{t.fleet.day}</span></div>
                    </div>
                    <span className="badge-brutal bg-brand text-topacio-ink shrink-0 self-start">{selected.group}</span>
                  </div>
                )}
                <div>
                  <label className={labelCls}>{t.booking.pickDates}</label>
                  <div className="border-2 border-topacio-ink rounded-lg p-2 sm:p-3 bg-white flex justify-center overflow-x-auto" data-testid="booking-calendar">
                    <DayPicker mode="range" selected={range} onSelect={setRange}
                      disabled={[{ before: today }, isClosedDate, (d) => closedDates.has(iso(d)), ...booked]}
                      locale={LOCALES[lang] || es} numberOfMonths={1} />
                  </div>
                  <div className="flex gap-5 mt-3 text-xs font-bold text-topacio-faded">
                    <span className="flex items-center gap-2">
                      <span className="w-3.5 h-3.5 bg-brand border-2 border-topacio-ink rounded-sm inline-block" />{t.booking.legendFree}
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="w-3.5 h-3.5 bg-white border-2 border-topacio-red rounded-sm inline-block line-through text-center text-topacio-red text-[8px] leading-[10px]">x</span>{t.booking.legendBusy}
                    </span>
                    {days > 0 && <span data-testid="booking-days-label" className="ml-auto text-topacio-ink">{nfDays(days)} {t.booking.days}</span>}
                  </div>
                </div>
                <div>
                  <label className={labelCls}>{t.booking.pickup}</label>
                  {deliveryEnabled ? (
                    <>
                      <select data-testid="booking-pickup-select" value={pickup}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === "hotel" && !hotelName) {
                            setHotelOpen(true);
                          } else {
                            setPickup(v);
                            if (v !== "hotel") setHotelName("");
                          }
                        }} className="input-brutal">
                        <option value="office">{t.booking.pickupOffice}</option>
                        <option value="airport">{t.booking.pickupAirport}</option>
                        <option value="hotel">{t.booking.pickupHotel}</option>
                      </select>
                      {pickup === "hotel" && hotelName && (
                        <button type="button" data-testid="hotel-edit-btn" onClick={() => setHotelOpen(true)}
                          className="mt-2 flex items-center gap-2 text-xs font-extrabold text-topacio-ink bg-brand border-2 border-topacio-ink rounded-lg px-3 py-2 shadow-[2px_2px_0_0_#111] hover:-translate-y-0.5 transition-transform">
                          <Truck className="w-4 h-4" /> {t.booking.hotelSummary}: {hotelName}
                        </button>
                      )}
                    </>
                  ) : (
                    <div data-testid="office-pickup-only" className="input-brutal bg-topacio-sand flex items-center gap-3">
                      <span className="w-9 h-9 bg-brand border-2 border-topacio-ink rounded-lg flex items-center justify-center shrink-0"><Store className="w-4 h-4" /></span>
                      <div>
                        <p className="font-extrabold text-topacio-ink text-sm">{t.booking.pickupOffice}</p>
                        <p className="text-[11px] font-semibold text-topacio-faded">{x.officeOnly}</p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>{x.pickupTime}</label>
                    <select data-testid="pickup-time-select" value={pickupTime} onChange={(e) => setPickupTime(e.target.value)} className="input-brutal">
                      {hoursForDate(range?.from).map((h) => <option key={h} value={h}>{h} h</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>{x.returnTime}</label>
                    <select data-testid="return-time-select" value={returnTime} onChange={(e) => setReturnTime(e.target.value)} className="input-brutal">
                      {hoursForDate(range?.to).map((h) => <option key={h} value={h}>{h} h</option>)}
                    </select>
                  </div>
                </div>
                <a data-testid="other-hours-whatsapp" href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noreferrer"
                  className="flex items-center justify-center gap-2 text-[11px] font-extrabold text-topacio-ink hover:text-topacio-red transition-colors">
                  <MessageCircle className="w-3.5 h-3.5 text-[#25D366]" /> {x.otherHours} →
                </a>
                <p data-testid="closed-days-note" className="text-center text-[10px] font-semibold text-topacio-faded -mt-1">{x.closedDay}</p>
                <div>
                  <label className={labelCls}>{t.booking.extras}</label>
                  {checkResult?.ok && checkResult.noFullInsurance && (
                    <a data-testid="under25-whatsapp-banner" href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noreferrer"
                      className="mb-2 flex items-center gap-2.5 rounded-lg border-2 border-[#25D366] bg-[#25D366]/10 px-4 py-3 text-xs font-extrabold text-topacio-ink hover:-translate-y-0.5 transition-transform">
                      <MessageCircle className="w-4 h-4 text-[#25D366] shrink-0" /> {x.under25}: +34 630 57 90 80
                    </a>
                  )}
                  <div className="grid grid-cols-1 gap-2">
                    {EXTRA_KEYS.filter((k) => k !== "delivery" && k !== "topcase").map((k) => {
                      const meta = EXTRAS_META[k];
                      const insDay = k === "full_insurance" ? (selected?.insurance_per_day ?? meta.per_day) : meta.per_day;
                      const priceTxt = meta.short_flat != null
                        ? (billing.tierDays > 2 ? x.includedFree : `${fmt(meta.short_flat)} ${t.booking.once}`)
                        : meta.flat ? `${fmt(meta.flat)} ${t.booking.once}` : `${fmt(insDay)}${t.booking.perDay}`;
                      const blocked = k === "full_insurance" && checkResult?.ok && checkResult.noFullInsurance;
                      const active = extras.includes(k) && !blocked;
                      const iconCfg = EXTRA_ICONS[k];
                      const EIcon = iconCfg?.Icon;
                      return (
                        <button key={k} data-testid={`extra-${k}`} disabled={blocked}
                          onClick={() => { if (blocked) { toast.error(x.under25NoInsurance); return; } toggleExtra(k); }}
                          title={blocked ? x.under25NoInsurance : undefined}
                          className={`flex items-center gap-3 px-4 py-3.5 rounded-lg border-2 text-sm font-semibold transition-[transform,box-shadow,background-color] ${blocked ? "border-zinc-200 text-zinc-300 opacity-50 cursor-not-allowed" : active ? "border-topacio-ink bg-brand text-topacio-ink shadow-[3px_3px_0_0_#111]" : "border-zinc-200 text-topacio-faded hover:border-topacio-ink"}`}>
                          <span className={`w-9 h-9 shrink-0 flex items-center justify-center rounded-lg border-2 transition-colors ${active ? "bg-white border-topacio-ink" : "bg-topacio-sand border-zinc-200"}`}>
                            {EIcon && (iconCfg.svg
                              ? <EIcon className="w-5 h-5 text-topacio-ink" visor={active ? "#E63946" : "#FFCC00"} />
                              : <EIcon className="w-5 h-5 text-topacio-ink" strokeWidth={2.2} />)}
                          </span>
                          <span className="flex-1 text-left">
                            {t.booking.extrasList[k]}
                            {k === "helmet2" && <span className="block text-[10px] font-bold text-topacio-faded normal-case">{x.helmet2Note}</span>}
                            {k === "gloves" && <span className="block text-[10px] font-bold text-topacio-faded normal-case">{x.glovesNote}</span>}
                            {k === "full_insurance" && selected?.franchise > 0 && <span data-testid="insurance-franchise-note" className="block text-[10px] font-bold text-topacio-red normal-case">{x.franchiseLabel}: {fmt(selected.franchise)}</span>}
                          </span>
                          <span className={`font-extrabold ${active ? "text-topacio-ink" : blocked ? "text-zinc-300" : "text-topacio-red"}`}>{priceTxt}</span>
                        </button>
                      );
                    })}
                  </div>
                  <p data-testid="booking-safety-note" className="mt-3 flex items-start gap-2 text-[11px] font-bold text-topacio-ink bg-topacio-sand border-2 border-topacio-ink/15 rounded-lg px-3 py-2 leading-snug">
                    <ShieldAlert className="w-4 h-4 text-topacio-red shrink-0 mt-0.5" /> {x.safetyNote}
                  </p>
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-5">
                <div>
                  <label className={labelCls}>{t.booking.name}</label>
                  <input data-testid="booking-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-brutal" />
                </div>
                <div>
                  <label className={labelCls}>{t.booking.email}</label>
                  <input data-testid="booking-email-input" type="email" value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    onBlur={(e) => checkLoyalty(e.target.value)}
                    className="input-brutal" />
                  {loyalty?.returning && (
                    <p data-testid="loyalty-badge" className="mt-2 flex items-center gap-1.5 text-xs font-extrabold text-topacio-green uppercase tracking-wider">
                      <HeartHandshake className="w-4 h-4" /> {x.loyalty}
                    </p>
                  )}
                </div>
                <div>
                  <label className={labelCls}>{t.booking.phone}</label>
                  <input data-testid="booking-phone-input" inputMode="tel" placeholder="+34 630 57 90 80" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-brutal" />
                  <p className="mt-1.5 text-[11px] font-semibold text-topacio-faded">{x.fastCheck.phonePrefix}</p>
                </div>
                <div>
                  <label className={labelCls}>{t.booking.notes}</label>
                  <textarea data-testid="booking-notes-input" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="input-brutal" />
                </div>

                <div className="border-2 border-topacio-ink rounded-lg overflow-hidden" data-testid="fastcheck-card">
                  <div className="bg-topacio-ink px-4 py-2.5 flex items-center gap-2.5">
                    <IdCard className="w-4 h-4 text-brand" />
                    <span className="text-[11px] uppercase tracking-wider font-extrabold text-white">{x.fastCheck.title}</span>
                    <span className="ml-auto badge-brutal bg-topacio-red text-white !text-[9px]">{selected?.license}</span>
                  </div>
                  <div className="p-4 bg-topacio-sand space-y-3">
                    <p className="text-[11px] text-topacio-faded font-semibold">{x.fastCheck.sub}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className={labelCls}>{x.fastCheck.birthdate}</label>
                        <input data-testid="fastcheck-birthdate" type="date" value={check.birthdate} max={iso(today)}
                          onChange={(e) => setCheck({ ...check, birthdate: e.target.value })} className="input-brutal !px-3 !py-2.5" />
                      </div>
                      <div>
                        <label className={labelCls}>{x.fastCheck.licenseType}</label>
                        <select data-testid="fastcheck-license-type" value={check.licenseType}
                          onChange={(e) => setCheck({ ...check, licenseType: e.target.value })} className="input-brutal !px-3 !py-2.5">
                          <option value="">{x.fastCheck.selectType}</option>
                          {LICENSE_TYPES.map((l) => <option key={l} value={l}>{l}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className={labelCls}>{x.fastCheck.licenseYear}</label>
                        <input data-testid="fastcheck-license-year" type="number" inputMode="numeric" min="1950" max={currentYear} placeholder="2019"
                          value={check.licenseYear} onChange={(e) => setCheck({ ...check, licenseYear: e.target.value })} className="input-brutal !px-3 !py-2.5" />
                      </div>
                    </div>
                    {checkResult == null ? (
                      <p data-testid="fastcheck-incomplete" className="flex items-center gap-2 text-xs font-bold text-topacio-faded">
                        <IdCard className="w-4 h-4" /> {x.fastCheck.incomplete}
                      </p>
                    ) : (
                      <p data-testid="fastcheck-result"
                        className={`flex items-center gap-2 text-xs font-extrabold uppercase tracking-wider ${checkResult.ok ? "text-topacio-green" : "text-topacio-red"}`}>
                        {checkResult.ok ? <ShieldCheck className="w-4 h-4" /> : <ShieldAlert className="w-4 h-4" />}
                        {checkMsg}
                      </p>
                    )}
                  </div>
                </div>

                <div data-testid="precheckin-notice" className="flex items-start gap-3 border-2 border-topacio-ink rounded-lg bg-brand/35 px-4 py-3">
                  <ShieldCheck className="w-5 h-5 text-topacio-green shrink-0 mt-0.5" />
                  <p className="text-xs font-bold text-topacio-ink leading-snug">{x.precheckinNotice}</p>
                </div>
              </div>
            )}

            {step === 2 && selected && (
              <div>
                <div className="flex gap-4 items-center border-2 border-topacio-ink rounded-lg p-4 bg-topacio-sand">
                  <img src={selected.image_path ? `${API}/bikes/image/${selected.slug}` : selected.image} alt={selected.name} className="w-24 h-16 object-cover rounded-md border-2 border-topacio-ink bg-white" />
                  <div>
                    <div className="font-display font-bold text-topacio-ink text-sm">{selected.name}</div>
                    <div className="text-xs text-topacio-faded font-semibold mt-1">{start} → {end} · {nfDays(days)} {t.booking.days}</div>
                  </div>
                </div>

                <div className="mt-5">
                  <label className={labelCls}>{t.booking.couponLabel}</label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <TicketPercent className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-topacio-faded" />
                      <input data-testid="coupon-input" value={couponInput} onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                        placeholder={t.booking.couponPlaceholder}
                        className="input-brutal !pl-9 uppercase" disabled={!!coupon} />
                    </div>
                    <button data-testid="coupon-apply-btn" onClick={applyCoupon} disabled={!!coupon}
                      className="btn-secondary !px-4 !py-2.5 !text-xs">
                      {t.booking.couponApply}
                    </button>
                  </div>
                  {coupon && (
                    <p data-testid="coupon-applied-msg" className="mt-2 text-xs font-extrabold text-topacio-green uppercase tracking-wider">
                      {t.booking.couponApplied}: {coupon.code} {coupon.type === "percent" ? `-${coupon.value}%` : `-${coupon.value}€`}
                    </p>
                  )}
                </div>

                {pickup === "hotel" && hotelName && (
                  <div data-testid="booking-hotel-line" className="mt-3 flex items-center gap-2 text-xs font-extrabold text-topacio-ink bg-brand border-2 border-topacio-ink rounded-lg px-3 py-2.5">
                    <Truck className="w-4 h-4 shrink-0" /> {t.booking.hotelSummary}: {hotelName} (+25 €)
                  </div>
                )}
                {selected && (avail.stock || 1) > 1 && unitsLeft != null && unitsLeft <= Math.min(2, avail.stock) && (
                  unitsLeft === 1 ? (
                    <div data-testid="stock-last-unit" className="mt-3 flex items-center gap-2 text-xs font-extrabold text-white bg-topacio-red border-2 border-topacio-ink rounded-lg px-3 py-2.5 animate-pulse">
                      <Flame className="w-4 h-4 shrink-0" /> {x.lastUnit}
                    </div>
                  ) : (
                    <div data-testid="stock-left" className="mt-3 flex items-center gap-2 text-xs font-extrabold text-topacio-ink bg-brand border-2 border-topacio-ink rounded-lg px-3 py-2.5">
                      <Flame className="w-4 h-4 shrink-0" /> {x.unitsLeft.replace("{n}", unitsLeft)}
                    </div>
                  )
                )}
                {price && (
                  <div className="mt-5 space-y-2 text-sm font-semibold" data-testid="booking-summary">
                    <div className="flex justify-between text-topacio-faded"><span>{t.booking.rental} ({nfDays(days)} {x.daysUnit})</span><span>{fmt(price.rental)}</span></div>
                    {billing.extra > 0 && (
                      <div data-testid="billing-breakdown" className="text-[11px] text-topacio-faded -mt-1 font-semibold">
                        {x.billedAs} · {billing.hours} h → {billing.fullDays > 0 ? `${billing.fullDays}×24h + ` : ""}{billing.extra === 0.5 ? "½" : billing.extra === 0.75 ? "¾" : "1"} {x.daysUnit}
                      </div>
                    )}
                    <div className="flex justify-between text-topacio-faded"><span>{t.booking.extrasTotal}</span><span>{fmt(price.extrasTotal)}</span></div>
                    {price.discount > 0 && (
                      <div data-testid="booking-discount-row" className="flex justify-between text-topacio-green font-extrabold"><span>{t.booking.discount}</span><span>-{fmt(price.discount)}</span></div>
                    )}
                    {price.loyalty > 0 && (
                      <div data-testid="booking-loyalty-row" className="flex justify-between text-topacio-green font-extrabold"><span>{x.loyalty}</span><span>-{fmt(price.loyalty)}</span></div>
                    )}
                    <div className="flex justify-between font-display font-black text-topacio-ink text-lg pt-3 border-t-2 border-dashed border-zinc-200"><span>{t.booking.total}</span><span className="text-topacio-red">{fmt(price.total)}</span></div>
                    <div data-testid="booking-advance-row" className="flex justify-between text-topacio-ink font-extrabold text-sm pt-1"><span>{x.advanceNow}</span><span className="text-topacio-green">{fmt(Math.round(price.total * 0.5 * 100) / 100)}</span></div>
                    <div className="flex justify-between text-topacio-faded text-xs"><span>{x.restOnPickup}</span><span>{fmt(price.total - Math.round(price.total * 0.5 * 100) / 100)}</span></div>
                  </div>
                )}
                <p className="mt-4 text-xs text-topacio-faded">{t.booking.depositNote}</p>
                <p data-testid="rental-terms-note" className="mt-2 text-xs font-bold text-topacio-ink bg-topacio-sand border-2 border-topacio-ink/15 rounded-lg px-3 py-2">
                  {x.rentalTermsNote.replace("{deposit}", selected.deposit).replace("{fee}", selected.cancellation_fee ?? 50)}
                </p>

                <label data-testid="terms-checkbox-label" className="mt-5 flex items-start gap-3 cursor-pointer select-none">
                  <input data-testid="terms-checkbox" type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)}
                    className="w-5 h-5 mt-0.5 shrink-0 accent-[#FFCC00] cursor-pointer" />
                  <span className="text-sm text-topacio-faded leading-snug">
                    {t.booking.termsPre}{" "}
                    <Link to="/condiciones" target="_blank" data-testid="terms-link" className="font-extrabold text-topacio-red underline underline-offset-2 hover:text-topacio-ink">
                      {t.booking.termsLink}
                    </Link>
                  </span>
                </label>

                <label data-testid="privacy-checkbox-label" className="mt-3 flex items-start gap-3 cursor-pointer select-none">
                  <input data-testid="privacy-checkbox" type="checkbox" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)}
                    className="w-5 h-5 mt-0.5 shrink-0 accent-[#E63946] cursor-pointer" />
                  <span className="text-sm text-topacio-faded leading-snug">
                    {x.privacyPre}{" "}
                    <Link to="/privacidad" target="_blank" data-testid="privacy-link" className="font-extrabold text-topacio-red underline underline-offset-2 hover:text-topacio-ink">
                      {x.privacyLink}
                    </Link>
                    {x.privacyPost ? ` ${x.privacyPost}` : ""}
                  </span>
                </label>
              </div>
            )}

            <div className="sticky bottom-0 -mx-5 sm:-mx-6 -mb-5 sm:-mb-6 mt-8 px-5 sm:px-6 py-4 bg-white border-t-2 border-topacio-ink flex items-center justify-between gap-3">
              {step > 0 ? (
                <button data-testid="booking-back-btn" onClick={() => setStep(step - 1)}
                  className="flex items-center gap-1 text-sm font-bold text-topacio-faded hover:text-topacio-ink transition-colors">
                  <ChevronLeft className="w-4 h-4" /> {t.booking.back}
                </button>
              ) : <span />}
              {step < 2 ? (
                <button data-testid="booking-next-btn" onClick={next} className="btn-primary !px-6 !py-3.5 !text-xs">
                  {t.booking.next}
                </button>
              ) : (
                <div className="flex w-full sm:w-auto">
                  <button data-testid="booking-pay-btn" disabled={busy || !terms || !privacy} onClick={() => submit(true)} className="btn-red w-full sm:w-auto !px-6 !py-3.5 !text-xs">
                    {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                    {t.booking.payDeposit}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        {hotelOpen && (
          <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="hotel-modal">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => { if (pickup !== "hotel") setHotelOpen(false); }} />
            <div className="relative card-brutal w-full max-w-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-10 h-10 shrink-0 bg-brand border-2 border-topacio-ink rounded-lg flex items-center justify-center shadow-[2px_2px_0_0_#111]">
                  <Truck className="w-5 h-5 text-topacio-ink" />
                </span>
                <h4 className="font-display font-bold text-topacio-ink leading-tight">{t.booking.hotelTitle}</h4>
              </div>
              <p className="text-xs text-topacio-faded font-semibold mb-4">{t.booking.hotelSub}</p>
              <input data-testid="hotel-name-input" value={hotelName} onChange={(e) => setHotelName(e.target.value)}
                placeholder={t.booking.hotelName} className="input-brutal" autoFocus
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); document.querySelector('[data-testid="hotel-confirm-btn"]')?.click(); } }} />
              <div className="flex gap-3 mt-5">
                <button type="button" data-testid="hotel-cancel-btn"
                  onClick={() => { setHotelOpen(false); if (!hotelName) setPickup("office"); }}
                  className="btn-secondary flex-1 !py-3 !text-xs">
                  {t.booking.back}
                </button>
                <button type="button" data-testid="hotel-confirm-btn"
                  onClick={() => {
                    if (!hotelName.trim()) { toast.error(t.booking.hotelRequired); return; }
                    setPickup("hotel");
                    setHotelOpen(false);
                  }}
                  className="btn-primary flex-1 !py-3 !text-xs">
                  {t.booking.hotelSave}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
